import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useSetRecoilState } from 'recoil';

import { labelData } from 'views/complex-onboarding-flow/constant';
import { useKycActionState } from 'views/complex-onboarding-flow/hooks';
import { useRecoilValue } from 'recoil';
import { PipelineMasterSteps } from 'global-stores';
import { ActionList } from 'views/complex-onboarding-flow/constant';
import { ComplexSettingFormState } from 'views/pipeline';
import { CustomHandle } from '../custom-handle';
import { ICustomNode } from '../custom-node';
import { RenderBlock } from './render-block';
import {
	billedPrice,
	findAmlAction,
	kycNodeTotal,
} from 'views/complex-onboarding-flow/pricing/kyc-pricing';
// __simplici_kyc_validation_checkboxes__: custom-node imports this file directly
// (`../kyc-action/kyc-action`), bypassing index.ts -- which was the only place
// the stylesheet was pulled in, so it never loaded. Import it here so the styles
// follow the component whichever path is used.
import './kyc-action.scss';
import { TileTotal } from '../tile-total';

/** The one tile row whose position is fixed by hand — see renderActionLayout. */
const CRIMINAL_ROW = 'criminalBackgroundCheck';

export const KycWrapper = ({ children }: { children: ReactNode }) => {
	return <div className="kyc-action">{children}</div>;
};

const KycAction: FC<ICustomNode> = ({ id: nodeId, data }) => {
	const { StepIds, kycStepId } = useKycActionState(data.label, nodeId);
	const actionId = useMemo(() => data.label, [data.label]);
	const setComplexSettingForm = useSetRecoilState(ComplexSettingFormState);

	// Seed the KYC provider selection ("Business default") so it persists even if the KYC config
	// modal is never opened. Merge (never overwrite) so the rule/toggle config is untouched.
	useEffect(() => {
		setComplexSettingForm((prev: any) => {
			const next = structuredClone(prev);
			if (!next[nodeId]) {
				next[nodeId] = {};
			}
			// __simplici_kyc_validation_checkboxes__: liveliness must be seeded HERE
			// too. toggle-component only seeds a metadata default when the node has
			// no settings yet (`if (id && !complexSettingForm?.[id])`) -- and this
			// effect creates that object first, so the guard fails and liveliness
			// would never get a value at all (undefined => toggle reads off).
			if (next[nodeId].liveliness == null) {
				next[nodeId].liveliness = true;
			}
			// __simplici_kyc_validation_checkboxes__: seed both checks ON, matching
			// the catalog defaults and kyc.api's own defaults. Merge, never
			// overwrite -- a business that has already unchecked one keeps it.
			if (next[nodeId].documentValidation == null) {
				next[nodeId].documentValidation = true;
			}
			if (next[nodeId].dataValidation == null) {
				next[nodeId].dataValidation = true;
			}
			// __simplici_criminal_background_check__: seeded OFF, and seeded
			// EXPLICITLY rather than left unset — $5 per applicant is not something
			// to opt a flow into by omission, and an explicit `false` is what
			// publish serialises, so kyc.api is told not to run it rather than left
			// to infer it.
			if (next[nodeId].criminalBackgroundCheck == null) {
				next[nodeId].criminalBackgroundCheck = false;
			}
			// __simplici_kyc_aml_single_tile__: PEPs & Sanctions is seeded ON with the
			// other two, so all three checks on the KYC & AML tile start ticked.
			// NOTE this bills $0.35 per verification on every KYC step unless it is
			// unticked -- the AML step used to be opt-in, added by dragging its own
			// card. Merge, never overwrite, so a flow that has already unticked it
			// keeps that choice.
			if (next[nodeId].amlVerification == null) {
				next[nodeId].amlVerification = true;
			}
			if (next[nodeId].kycProvider == null) {
				next[nodeId].kycProvider = {
					label: 'Business default',
					value: 'businessDefault',
				};
			}
			return next;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nodeId, setComplexSettingForm]);

	/* __simplici_criminal_background_check__: drawn LAST, under PEPs & Sanctions,
	   so it is the bottom row of the tile. Its position in the catalog would
	   otherwise put it above PEPs — the PEPs row is not part of this mapped list
	   at all, it is appended after it (see __simplici_kyc_aml_single_tile__) — so
	   the one row that has to be last is held out and rendered there instead of
	   reordering a row that already ships. */
	const renderActionLayout = useMemo(
		() =>
			StepIds?.filter(
				({ labelId }: { labelId: string }) => labelId !== CRIMINAL_ROW
			).map(({ labelId }: { labelId: string }) => (
				<RenderBlock
					stepId={kycStepId}
					label={labelData[labelId]}
					key={labelId}
					labelId={labelId}
					nodeId={nodeId}
					actionId={actionId}
				/>
			)),
		[StepIds, kycStepId, nodeId, actionId]
	);

	/* Rendered only when the catalog actually ships the row — a panel pointed at
	   an older catalog draws the tile it has always drawn. */
	const hasCriminalRow = useMemo(
		() => StepIds?.some(({ labelId }: { labelId: string }) => labelId === CRIMINAL_ROW),
		[StepIds]
	);

	/* What this node BILLS — computed by the same function the canvas, the
	   configuration panel and Publish use (pricing/kyc-pricing.ts), so the four
	   cannot disagree. It used to be computed here on its own: metadata prices for
	   three hardcoded keys with no alias for an older catalog, so on dev2 it found
	   none of them and totalled AML alone ($0.37) while the rows above it, which
	   fell back to a different price, read $0.06 each. */
	const pipelinesSteps = useRecoilValue(PipelineMasterSteps);
	const complexSettingForm = useRecoilValue(ComplexSettingFormState);
	const kycMetadata = useMemo(
		() =>
			((pipelinesSteps.find((el: any) => el.key === ActionList[actionId])
				?.actions?.[0] as any)?.metadata ?? []) as any[],
		[pipelinesSteps, actionId]
	);

	// __simplici_kyc_aml_single_tile__: AML lives on the KYC tile but is priced
	// from the catalog's own `aml` step — the KYC metadata never carries it.
	const amlAction = useMemo(() => findAmlAction(pipelinesSteps as any), [pipelinesSteps]);
	const amlPrice = billedPrice(amlAction);

	const total = useMemo(
		() =>
			kycNodeTotal({
				metadata: kycMetadata,
				settings: complexSettingForm?.[nodeId],
				amlAction,
			}),
		[kycMetadata, complexSettingForm, nodeId, amlAction]
	);

	const amlOn = complexSettingForm?.[nodeId]?.amlVerification === true;
	// setComplexSettingForm is already declared above by the KYC checkbox rows.
	const toggleAml = useCallback(() => {
		setComplexSettingForm((prev: any) => {
			const next = structuredClone(prev);
			if (!next[nodeId]) next[nodeId] = {};
			next[nodeId].amlVerification = next[nodeId].amlVerification !== true;
			return next;
		});
	}, [nodeId, setComplexSettingForm]);

	return (
		<KycWrapper>
			<CustomHandle value={nodeId} />
			{renderActionLayout}
			{/* __simplici_kyc_aml_single_tile__: PEPs & Sanctions runs AFTER the KYC
				checks above -- publish emits it as its own stepId:'aml' node wired
				downstream, which is what actually orders the two. */}
			<label
				className="kyc-action__plain-row nodrag"
				onClick={e => e.stopPropagation()}
			>
				<input
					type="checkbox"
					className="nodrag"
					checked={amlOn}
					onChange={toggleAml}
					aria-label="Global PEPs & Sanctions"
				/>
				<span className="kyc-action__plain-label">Global PEPs &amp; Sanctions</span>
				{!!amlPrice && (
					<span className="kyc-action__plain-price">
						${amlPrice.toFixed(2)}
						<span className="kyc-action__plain-unit">/Verification</span>
					</span>
				)}
			</label>
			{hasCriminalRow && (
				<RenderBlock
					stepId={kycStepId}
					label={labelData[CRIMINAL_ROW]}
					labelId={CRIMINAL_ROW}
					nodeId={nodeId}
					actionId={actionId}
				/>
			)}
			{/* `total` already includes PEPs & Sanctions when it is ticked. */}
			<TileTotal amount={total} nodeId={nodeId} />
		</KycWrapper>
	);
};

export default KycAction;
